import { Button, Tag, Text } from "@chakra-ui/react";
import { ReactElement, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { displaySum } from "../../../common/formatting/displaySum";
import { CustomContractDataFieldDto } from "../../../autogen/bff-api";
import {
  isCustomAmountField,
  isCustomBooleanField,
  isCustomDateField,
  isCustomEmailField,
  isCustomEnumField,
  isCustomMultiEnumField,
  isCustomNumberField,
  isCustomPercentageField,
  isCustomPersonField,
  isCustomPhoneField,
  isCustomRichTextField,
  isCustomTagField,
  isCustomTextField,
  isCustomUrlField,
} from "../../../common/types";

interface CustomContractFieldValueProps {
  field?: CustomContractDataFieldDto | null;
  onOpenRichTextPreview?: (content: string) => void;
}

export const CustomContractFieldValue = (props: CustomContractFieldValueProps): ReactElement => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { field, onOpenRichTextPreview } = props;

  const handleRichTextOpen = useCallback(() => {
    if (!field || !isCustomRichTextField(field) || !field.text) return;
    const contentString = typeof field.text === "string" ? field.text : JSON.stringify(field.text);
    if (onOpenRichTextPreview) onOpenRichTextPreview(contentString);
  }, [field, onOpenRichTextPreview]);

  if (!field) {
    return (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomAmountField(field)) {
    return field.amount !== null ? (
      <Text fontSize="sm">
        {displaySum({
          value: field.amount,
          currency: field.currency ?? "NOK",
        })}
      </Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomNumberField(field)) {
    return field.value !== null ? (
      <Text fontSize="sm">{field.value}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomPercentageField(field)) {
    return field.value !== null && field.value !== undefined ? (
      <Text fontSize="sm">{(field.value * 100).toFixed(1)}%</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomDateField(field)) {
    return field.date ? (
      <Text fontSize="sm">{displayDate(field.date)}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomEmailField(field)) {
    return field.email ? (
      <Text fontSize="sm">{field.email}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomBooleanField(field)) {
    return <Text fontSize="sm">{field.isChecked ? t("Yes") : t("No")}</Text>;
  }

  if (isCustomEnumField(field)) {
    return field.value ? (
      <Text fontSize="sm">{field.value}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomMultiEnumField(field)) {
    return field.values && field.values.length > 0 ? (
      <Text fontSize="sm">{field.values.join(", ")}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomPersonField(field)) {
    return field.person ? (
      <Text fontSize="sm">
        {field.person.firstName} {field.person.lastName}
      </Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomPhoneField(field)) {
    return field.number ? (
      <Text fontSize="sm">{field.number}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomRichTextField(field)) {
    if (field.text) {
      return (
        <Button size="xs" variant="outline" onClick={handleRichTextOpen}>
          {t("Click to view")}
        </Button>
      );
    }
    return (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomTextField(field)) {
    return field.text ? (
      <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" maxWidth="200px">
        {field.text}
      </Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomTagField(field)) {
    return field.tag ? (
      <Tag size="sm" variant="outline">
        {field.tag}
      </Tag>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  if (isCustomUrlField(field)) {
    return field.url ? (
      <Text fontSize="sm">{field.url}</Text>
    ) : (
      <Text fontSize="sm" fontStyle="italic" color="smMuted">
        {t("Not provided")}
      </Text>
    );
  }

  return (
    <Text fontSize="sm" fontStyle="italic" color="smMuted">
      {t("Not provided")}
    </Text>
  );
};
